import type {
  CaseFile,
  CaseHistoryEntry,
  CaseNote,
  CaseStatus,
} from "@/domains/cases/api/types";
import { SEED_CASES } from "@/domains/cases/mocks/cases";

let cases: Array<CaseFile> = SEED_CASES.map((c) => ({
  ...c,
  assetIds: [...c.assetIds],
  notes: [...c.notes],
  history: [...c.history],
}));

let nextCaseNumber = 9300;

function delay<T>(value: T, ms = 200): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(value), ms));
}

export async function fetchCases(): Promise<ReadonlyArray<CaseFile>> {
  return delay(cases);
}

export async function fetchCaseById(id: string): Promise<CaseFile | undefined> {
  return delay(cases.find((c) => c.id === id));
}

function replaceCase(updated: CaseFile): void {
  const idx = cases.findIndex((c) => c.id === updated.id);
  if (idx === -1) throw new Error(`Dossier niet gevonden: ${updated.id}`);
  cases = [...cases.slice(0, idx), updated, ...cases.slice(idx + 1)];
}

export async function addAssetToCase(
  caseId: string,
  assetId: string,
  actor: { id: string; name: string },
): Promise<CaseFile> {
  const current = cases.find((c) => c.id === caseId);
  if (!current) throw new Error(`Dossier niet gevonden: ${caseId}`);
  if (current.assetIds.includes(assetId)) return delay(current, 100);
  const historyEntry: CaseHistoryEntry = {
    id: `h-${caseId}-${Date.now()}`,
    timestamp: new Date().toISOString(),
    actorId: actor.id,
    actorName: actor.name,
    type: "assetAdded",
    description: `Asset ${assetId} toegevoegd aan dossier.`,
  };
  const updated: CaseFile = {
    ...current,
    assetIds: [...current.assetIds, assetId],
    history: [...current.history, historyEntry],
  };
  replaceCase(updated);
  return delay(updated, 200);
}

export async function removeAssetFromCase(
  caseId: string,
  assetId: string,
  actor: { id: string; name: string },
): Promise<CaseFile> {
  const current = cases.find((c) => c.id === caseId);
  if (!current) throw new Error(`Dossier niet gevonden: ${caseId}`);
  const historyEntry: CaseHistoryEntry = {
    id: `h-${caseId}-${Date.now()}`,
    timestamp: new Date().toISOString(),
    actorId: actor.id,
    actorName: actor.name,
    type: "assetRemoved",
    description: `Asset ${assetId} verwijderd van dossier.`,
  };
  const updated: CaseFile = {
    ...current,
    assetIds: current.assetIds.filter((id) => id !== assetId),
    history: [...current.history, historyEntry],
  };
  replaceCase(updated);
  return delay(updated, 200);
}

export async function addCaseNote(
  caseId: string,
  note: Omit<CaseNote, "id" | "createdAt">,
): Promise<CaseFile> {
  const current = cases.find((c) => c.id === caseId);
  if (!current) throw new Error(`Dossier niet gevonden: ${caseId}`);
  const newNote: CaseNote = {
    ...note,
    id: `note-${caseId}-${Date.now()}`,
    createdAt: new Date().toISOString(),
  };
  const historyEntry: CaseHistoryEntry = {
    id: `h-${caseId}-${Date.now()}`,
    timestamp: newNote.createdAt,
    actorId: note.authorId,
    actorName: note.authorName,
    type: "noteAdded",
    description:
      note.scope === "asset"
        ? `Opmerking toegevoegd op asset ${note.assetId}.`
        : "Opmerking toegevoegd op dossier.",
  };
  const updated: CaseFile = {
    ...current,
    notes: [...current.notes, newNote],
    history: [...current.history, historyEntry],
  };
  replaceCase(updated);
  return delay(updated, 200);
}

export async function updateCaseStatus(
  caseId: string,
  status: CaseStatus,
  actor: { id: string; name: string },
): Promise<CaseFile> {
  const current = cases.find((c) => c.id === caseId);
  if (!current) throw new Error(`Dossier niet gevonden: ${caseId}`);
  const historyEntry: CaseHistoryEntry = {
    id: `h-${caseId}-${Date.now()}`,
    timestamp: new Date().toISOString(),
    actorId: actor.id,
    actorName: actor.name,
    type: "statusChanged",
    description: `Status gewijzigd naar '${status}'.`,
  };
  const updated: CaseFile = {
    ...current,
    status,
    history: [...current.history, historyEntry],
  };
  replaceCase(updated);
  return delay(updated, 150);
}

type CreateFromRequestInput = {
  request: {
    id: string;
    title: string;
    location: { address: string };
    period: { startDate: string; endDate: string };
    motivation: { reason: string };
  };
  manager: { id: string; name: string };
  district?: string;
};

export async function createCaseFromRequest({
  request,
  manager,
  district = "",
}: CreateFromRequestInput): Promise<CaseFile> {
  const id = `case-${nextCaseNumber}`;
  const number = String(nextCaseNumber);
  nextCaseNumber += 1;
  const created: CaseFile = {
    id,
    number,
    title: request.title,
    status: "intake",
    requestId: request.id,
    managerId: manager.id,
    managerName: manager.name,
    startDate: request.period.startDate,
    endDate: request.period.endDate,
    address: request.location.address,
    district,
    reason: request.motivation.reason,
    assetIds: [],
    notes: [],
    history: [
      {
        id: `h-${id}-${Date.now()}`,
        timestamp: new Date().toISOString(),
        actorId: manager.id,
        actorName: manager.name,
        type: "created",
        description: `Dossier aangemaakt vanuit aanvraag ${request.id}.`,
      },
    ],
  };
  cases = [created, ...cases];
  return delay(created, 200);
}

export async function appendHistory(
  caseId: string,
  entry: Omit<CaseHistoryEntry, "id" | "timestamp">,
): Promise<CaseFile> {
  const current = cases.find((c) => c.id === caseId);
  if (!current) throw new Error(`Dossier niet gevonden: ${caseId}`);
  const historyEntry: CaseHistoryEntry = {
    ...entry,
    id: `h-${caseId}-${Date.now()}`,
    timestamp: new Date().toISOString(),
  };
  const updated: CaseFile = {
    ...current,
    history: [...current.history, historyEntry],
  };
  replaceCase(updated);
  return delay(updated, 100);
}
